import store from 'redux/store';
import { deleteLock } from 'redux/slices/locksSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditLock } from './edit/showEditLock';

type LockEntity = Extract<ActionEntity, { type: 'lock' }>;

const isAllLock = (entities: readonly ActionEntity[]): entities is readonly LockEntity[] => entities.every((entity) => entity.type === 'lock');

/**
 * Action definitions for locks. `createLock` / `addToTransportLock` are scoped;
 * `edit` (single-only), `delete` and `viewInstances` are contextual.
 *
 * `viewInstances` is the first action to use the `navigate` channel: it returns
 * `'disabled'` when `ctx.navigate` is absent, so it can't be fired into the
 * void. The navigate function is created by the in-router host and merely closed
 * over here, so it still works even when an action is fired from inside a dialog
 * (which is itself outside the router).
 */
export const lockActionDefinitions: ActionDefinitions = [
  {
    id: 'createLock',
    scope: 'type',
    label: 'New Lock',
    resolve: (ctx) => () => void showEditLock({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllLock(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditLock({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllLock(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteLock({ zone_name: entity.zoneName, name: entity.name })), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this lock' : `these ${entities.length} locks`}?`,
      });
    },
  },
  {
    id: 'viewInstances',
    label: 'View Node Instances',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllLock(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () =>
        navigate(
          `/automation/node_instances?lock_name=${encodeURIComponent(only.name)}&parentOnly=false&sortColumn=node_instance_id&sortOrder=asc&page=1&limit=100`
        );
    },
  },
  {
    id: 'addToTransportLock',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllLock(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'lock',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName} / ${single.name}` : 'Selected locks' }
      );
    },
  },
];
